/**
 * Bosch catalog — extended coverage (2026-05-14).
 *
 * Companion to `boschCatalog.ts`. This file fills gaps in Bosch coverage:
 *
 *  - B4512 intrusion panel (mid-tier 28-point B-Series)
 *  - Solution 2000 / 3000 (Australia/EMEA residential panels)
 *  - DIVAR hybrid 3000 / 5000 (16 IP + 16 analog hybrid NVRs)
 *  - DIVAR IP all-in-one 7000 2U/3U (128-channel high-channel-count NVR)
 *  - ISC-BPR2-W12 / ISC-BPQ2-W12 Blue Line Gen2 PIR (single + quad)
 *  - DS1101i / DS1102i / DS1108i acoustic glass-break (legacy SAT line)
 *  - ISC-GBD2-PS conventional glass-break (current EU SKU)
 *  - ISP-GBD2-P110S passive glass-break (LSN, VdS-B certified)
 *  - D7050 / D7050TH addressable photoelectric smoke heads
 *  - RFPB-SB / RFPB-TB / RFPB-FP RADION wireless panic transmitters
 *  - RFGB-A RADION wireless glass-break
 *  - RFSM-A RADION wireless smoke + heat
 *  - RFSM2-A RADION wireless smoke (current SKU)
 *  - DS936 / DS938Z / DS9360 panoramic ceiling PIR detectors
 *  - DS794Z classic long-range PIR (60 m mirror PIR)
 *  - AMC2 Access Modular Controller + 4WCF / 4R4CF reader variants
 *  - B810 RADION wireless SDI2 receiver (sits under keypad slot — wireless bus
 *    interface for RADION accessories)
 *
 * Note on user-requested SKUs that are NOT included:
 *
 *  - DIP-7184 / DIP-7192 — these are DIVAR IP 7000 SKU codes referencing
 *    specific HDD configurations of the same chassis (DIP-7184 = 2U / 4×4TB,
 *    DIP-7186 = 2U / 8×6TB). We ship one DIVAR IP 7000 2U entry and one 3U
 *    entry; the picker doesn't need every HDD-config SKU as a separate row.
 *  - DIVAR network 7000 — does not exist as a standalone product. Bosch's
 *    "7000" tier is the DIVAR IP (all-in-one) family. DIVAR network tops out
 *    at 5000 (already in boschCatalog.ts).
 *  - DS150 / DS160 — listed in the source brief as panic buttons. Per the
 *    Bosch datasheets, they are Request-to-Exit PIR sensors for access-
 *    control egress, NOT panic / hold-up buttons. They don't fit the
 *    'panic-button' category cleanly. We ship the RFPB-* RADION wireless
 *    panic transmitters (which actually ARE panic buttons) instead.
 *  - ISC-PDL1 / ISC-PDL1-W — Professional Series TriTech variants. The
 *    18-metre versions (ISC-PDL1-W18G / WA18G) are already in boschCatalog.ts.
 *
 * Every entry below cites a primary Bosch / KEENFINITY datasheet, an
 * authoritative product page, or an authorised distributor mirror of the
 * Bosch datasheet PDF. Fields left undefined when the source does not list
 * a value — never guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// Datasheet host shortcuts — Bosch Azure CDN + KEENFINITY commerce + Bosch
// media CDN (the three primary first-party hosts for Bosch datasheets).
const BOSCH_CDN =
  'https://resources-boschsecurity-cdn.azureedge.net/public/documents';
const BOSCH_ASSETS =
  'https://assets.catalog.boschbuildingtechnologies.com/public/documents';
const BOSCH_MEDIA =
  'https://media.boschsecurity.com/fs/media/pb/media/products_1/intrusion_alarm_systems';
const KEENFINITY_RES = 'https://resources.keenfinity.tech/public/documents';

export const BOSCH_EXTENDED: EquipmentCatalogEntry[] = [
  // ───────────────────────────────────────────────────────────────────────
  // ALARM CONTROL PANELS
  // ───────────────────────────────────────────────────────────────────────

  // B4512 — Bosch B-Series 28-point IP panel (mid-tier between B3512/B5512).
  // Source: B4512 datasheet on the Bosch CDN.
  // https://resources-boschsecurity-cdn.azureedge.net/public/documents/B4512_Control_Panel_Data_sheet_enUS_10683712395.pdf
  {
    id: 'bosch-b4512',
    manufacturer: 'Bosch',
    model: 'B4512',
    category: 'alarm-panel',
    tagline: '28-point B-Series IP panel — 2 areas, 32 users, on-board Ethernet',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/B4512_Control_Panel_Data_sheet_enUS_10683712395.pdf`,
    specs: {
      series: 'B-Series',
      zonesOnboard: 8,
      zonesMax: 28,
      partitions: 2,
      outputsOnboard: 3,
      outputsMax: 27,
      users: 32,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },

  // Solution 2000 — Bosch APAC/EMEA residential entry panel, 8 zones.
  // Source: Solution 2000/3000 brochure (Bosch media CDN) + commerce page.
  // https://media.boschsecurity.com/fs/media/pb/media/products_1/intrusion_alarm_systems/solution_2000___1/solution-2000-3000-brochure.pdf
  {
    id: 'bosch-solution-2000',
    manufacturer: 'Bosch',
    model: 'Solution 2000 (ICP-SOL2-P)',
    category: 'alarm-panel',
    tagline: '8-zone residential panel — 2 areas, 54 user codes, optional RADION wireless',
    verified: true,
    datasheetUrl: `${BOSCH_MEDIA}/solution_2000___1/solution-2000-3000-brochure.pdf`,
    specs: {
      series: 'Solution 2000',
      zonesOnboard: 8,
      zonesMax: 8,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 54,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      mobileApp: 'RSC+',
    },
  },

  // Solution 3000 — Bosch APAC/EMEA residential mid panel, 16 zones (8 split).
  // Source: Solution 2000/3000 brochure + Bosch commerce ICP-SOL3-P page.
  // https://media.boschsecurity.com/fs/media/pb/media/products_1/intrusion_alarm_systems/solution_2000___1/solution-2000-3000-brochure.pdf
  {
    id: 'bosch-solution-3000',
    manufacturer: 'Bosch',
    model: 'Solution 3000 (ICP-SOL3-P)',
    category: 'alarm-panel',
    tagline: '16-zone residential panel — 2 areas, 32 PINs + 32 fobs, RADION-ready',
    verified: true,
    datasheetUrl: `${BOSCH_MEDIA}/solution_2000___1/solution-2000-3000-brochure.pdf`,
    specs: {
      series: 'Solution 3000',
      zonesOnboard: 16,
      zonesMax: 16,
      partitions: 2,
      outputsOnboard: 4,
      outputsMax: 12,
      users: 64, // 32 PIN + 32 RADION keyfob slots
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      mobileApp: 'RSC+',
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // KEYPADS / WIRELESS BUS INTERFACES
  // ───────────────────────────────────────────────────────────────────────

  // B810 — RADION wireless SDI2 bus interface (receiver for all RADION
  // transmitters: detectors, keyfobs, panic, glass-break, smoke). Sits in the
  // keypad / bus-device slot since it shares the SDI2 bus with the keypads.
  // Source: B810 datasheet on the Bosch CDN (DS-B810K).
  // https://nbgtele.com/wp-content/uploads/2024/10/DS-B810K_Product-Data-Sheet.pdf
  {
    id: 'bosch-b810',
    manufacturer: 'Bosch',
    model: 'B810',
    category: 'keypad',
    tagline: 'RADION SDI2 wireless receiver — 504 points, 1000 keyfobs, dual antennas, 433.42 MHz',
    verified: true,
    datasheetUrl:
      'https://nbgtele.com/wp-content/uploads/2024/10/DS-B810K_Product-Data-Sheet.pdf',
    specs: {
      display: 'led',
      connection: 'SDI2 bus + 433.42 MHz RADION wireless',
      compatibility: ['B3512', 'B4512', 'B5512', 'B6512', 'B8512G', 'B9512G', 'Solution 2000', 'Solution 3000'],
      grade: 2,
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // NVR / DVR RECORDERS — HYBRID + DIVAR IP 7000
  // ───────────────────────────────────────────────────────────────────────

  // DIVAR hybrid 3000 (DDH-3532) — 16 IP + 16 analog hybrid recorder.
  // Source: DIVAR hybrid 3000 datasheet PDF (Bosch mirror via abbas.cz).
  // https://us.boschsecurity.com/en/products/videosystems/recording/iprecording/divarhybrid3000recorder/divarhybrid3000recorder_36813
  {
    id: 'bosch-divar-hybrid-3000',
    manufacturer: 'Bosch',
    model: 'DIVAR hybrid 3000 (DDH-3532)',
    category: 'nvr-recorder',
    tagline: '16 IP + 16 analog hybrid recorder — up to 12 MP IP, 960H analog, 1.5U',
    verified: true,
    datasheetUrl:
      'https://www.alldataresource.com/assets/images_bosch/Bosch_Security_Systems_DDH-3532-112D00_Specification_Sheet.pdf',
    specs: {
      channels: 32, // 16 IP + 16 analog co-existing
      maxResolution: '12 MP (IP) / 960H (analog)',
      hddBays: 2,
      poeInPorts: 0,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 1 GbE',
    },
  },

  // DIVAR hybrid 5000 (DRH-5532) — premium 16 IP + 16 analog hybrid recorder.
  // Source: DIVAR hybrid 5000 datasheet PDF.
  // https://www.commerce.boschsecurity.com/us/en/DIVAR-hybrid-5000-recorder/p/22797085579/
  {
    id: 'bosch-divar-hybrid-5000',
    manufacturer: 'Bosch',
    model: 'DIVAR hybrid 5000 (DRH-5532)',
    category: 'nvr-recorder',
    tagline: '16 IP + 16 analog hybrid recorder — 12 MP IP, 4 TB onboard, 1.5U, DVD-RW option',
    verified: true,
    datasheetUrl:
      'http://s3.abbas.cz/PRODUKT/Datasheet/Bosch/ENG/DIVAR_hybrid_5000_Data_sheet_enUS_27833115147.pdf',
    specs: {
      channels: 32, // 16 IP + 16 analog co-existing
      maxResolution: '12 MP (IP) / 960H (analog)',
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 0,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      networkPorts: '1× 1 GbE',
    },
  },

  // DIVAR IP all-in-one 7000 2U (DIP-7180 / DIP-7184 / DIP-7186 / DIP-7188).
  // Source: DIVAR IP 7000 2U datasheet on Bosch CDN (DIP-7000-2U).
  // https://cdn.commerce.boschsecurity.com/public/documents/DIVAR_IP_7000_2U_Data_sheet_enUS_83414001803.pdf
  {
    id: 'bosch-divar-ip-7000-2u',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 7000 2U (DIP-7180/7184/7186)',
    category: 'nvr-recorder',
    tagline: 'Up to 128 channels — 8 hot-swap bays, RAID 5, BVMS preinstalled, 2U rack',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/DIVAR_IP_7000_2U_Data_sheet_enUS_83414001803.pdf`,
    specs: {
      channels: 128,
      maxResolution: '4K',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5', '6', 'JBOD'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 1 GbE',
    },
  },

  // DIVAR IP all-in-one 7000 3U (DIP-71F0 / DIP-71F6). Higher-bay 3U chassis.
  // Source: same DIP-7000 family datasheet + B&H product page.
  // https://www.bhphotovideo.com/c/product/1340382-REG/bosch_dip_71f6_16hd_divar_ip_7000_series.html
  {
    id: 'bosch-divar-ip-7000-3u',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 7000 3U (DIP-71F0/71F6)',
    category: 'nvr-recorder',
    tagline: 'Up to 128 channels — 16 hot-swap bays, RAID 5/6, BVMS preinstalled, 3U rack',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/DIVAR_IP_7000_2U_Data_sheet_enUS_83414001803.pdf`,
    specs: {
      channels: 128,
      maxResolution: '4K',
      hddBays: 16,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5', '6', 'JBOD'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 1 GbE',
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // PIR MOTION DETECTORS — Blue Line Gen2 PIR + ceiling panoramic + classic
  // long-range. Existing catalog has the Blue Line Gen2 TriTech variants
  // (BDL2) and Professional Series PDL1; this adds the PIR-only Blue Line
  // variants plus the panoramic ceiling family and the DS794Z mirror PIR.
  // ───────────────────────────────────────────────────────────────────────

  // ISC-BPR2-W12 — Blue Line Gen2 PIR single-element, 12 m (40 ft) coverage.
  // Source: ISC-BPR2-W12 datasheet (Bosch via jmac mirror).
  // https://jmacfiles.s3.amazonaws.com/BOSCH-ISC-BPR2-W12-DataSheet.pdf
  {
    id: 'bosch-isc-bpr2-w12-pir',
    manufacturer: 'Bosch',
    model: 'ISC-BPR2-W12 (PIR-only)',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Blue Line Gen2 PIR — 12 m × 90°, dynamic temp compensation, no MW',
    verified: true,
    datasheetUrl: 'https://jmacfiles.s3.amazonaws.com/BOSCH-ISC-BPR2-W12-DataSheet.pdf',
    powerVMin: 9,
    powerVMax: 15,
    currentMaTypical: 10,
  },

  // ISC-BPQ2-W12 — Blue Line Gen2 Quad PIR, 12 m (40 ft) coverage.
  // Source: ISC-BPQ2-W12 datasheet (KEENFINITY resources mirror).
  // https://resources.keenfinity.tech/public/documents/ISC_BPQ2_W12_Data_sheet_enUS_2603527307.pdf
  {
    id: 'bosch-isc-bpq2-w12',
    manufacturer: 'Bosch',
    model: 'ISC-BPQ2-W12',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Blue Line Gen2 Quad PIR — 12 m, two opposed PIRs, false-alarm rejection',
    verified: true,
    datasheetUrl: `${KEENFINITY_RES}/ISC_BPQ2_W12_Data_sheet_enUS_2603527307.pdf`,
    powerVMin: 9,
    powerVMax: 15,
    currentMaTypical: 10,
  },

  // DS9360 — Panoramic TriTech ceiling detector, 360° × 18 m (60 ft) diameter.
  // Source: DS9360 datasheet (Bosch CDN).
  // https://resources-boschsecurity-cdn.azureedge.net/public/documents/DS9360_datasheet_Data_sheet_enUS_2630175499.pdf
  {
    id: 'bosch-ds9360',
    manufacturer: 'Bosch',
    model: 'DS9360',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 18,
    hfovDeg: 360,
    ceilingDiameterByHeightM: {
      2.4: 8,
      3.6: 12,
      4.8: 15,
      5.5: 18,
    },
    mountingHeightDefaultM: 3.6,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 5.5,
    mounting: ['ceiling'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'Panoramic TriTech ceiling — 360° × 18 m diameter, Motion Analyzer II',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/DS9360_datasheet_Data_sheet_enUS_2630175499.pdf`,
  },

  // DS936 — Low-profile panoramic PIR-only ceiling detector.
  // Source: DS936 datasheet PDF.
  // https://cbdigitaletl-prod.s3.amazonaws.com/BigCommerceProduct/ProductPdfs/DS936_Series_Datasheet.pdf
  {
    id: 'bosch-ds936',
    manufacturer: 'Bosch',
    model: 'DS936',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 7.5,
    hfovDeg: 360,
    ceilingDiameterByHeightM: {
      2.4: 7.5,
      3.0: 7.5,
      3.6: 7.5,
    },
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 3.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    tagline: 'Low-profile panoramic PIR — 360° × 7.5 m (24 ft) ceiling',
    verified: true,
    datasheetUrl:
      'https://cbdigitaletl-prod.s3.amazonaws.com/BigCommerceProduct/ProductPdfs/DS936_Series_Datasheet.pdf',
  },

  // DS938Z — Panoramic mirror TriTech ceiling, 360° × 18 m.
  // Source: DS938Z product page (Bosch AU mirror via esecuritysales) + spec
  // sheets confirm 360° × 18 m diameter at up to ~5.5 m mounting.
  // https://www.esecuritysales.com/product/bosch-360-x-18m-tritech-mirror-pir-detector-ds938z-e43272/
  {
    id: 'bosch-ds938z',
    manufacturer: 'Bosch',
    model: 'DS938Z',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 18,
    hfovDeg: 360,
    ceilingDiameterByHeightM: {
      2.4: 8,
      3.6: 12,
      4.8: 15,
      5.5: 18,
    },
    mountingHeightDefaultM: 3.6,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 5.5,
    mounting: ['ceiling'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'Mirror TriTech ceiling — 360° × 18 m diameter, false-alarm immunity',
    verified: true,
    datasheetUrl:
      'https://www.esecuritysales.com/product/bosch-360-x-18m-tritech-mirror-pir-detector-ds938z-e43272/',
  },

  // DS794Z — Classic long-range mirror PIR. Field-swappable optics deliver
  // three coverage patterns: 60 m × 3 m (OA200), 24 m × 15 m (OA80), and
  // 36 m × 7.6 m (OA120). Surface or corner mount.
  // Source: DS794Z datasheet (Bosch via A1 Security Cameras mirror).
  // https://www.a1securitycameras.com/content/product_documents/14241/bosch-Data_sheet_enUS_2633819787-A1.pdf
  {
    id: 'bosch-ds794z',
    manufacturer: 'Bosch',
    model: 'DS794Z',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 60,
    rangeStepsM: [60, 36, 24],
    hfovDeg: 15, // narrow corridor (OA200 mirror); broader optics widen to ~50°
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 3.6,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Classic long-range PIR — 60 m corridor / 24 m broad / 36 m intermediate',
    verified: true,
    datasheetUrl:
      'https://www.a1securitycameras.com/content/product_documents/14241/bosch-Data_sheet_enUS_2633819787-A1.pdf',
    powerVMin: 6,
    powerVMax: 15,
    currentMaTypical: 18,
  },

  // ───────────────────────────────────────────────────────────────────────
  // GLASS-BREAK DETECTORS
  // ───────────────────────────────────────────────────────────────────────

  // DS1101i — Round acoustic glass-break, wall or ceiling mount, Form-C relay.
  // Source: DS1100i Series datasheet (Bosch mirror barcodesinc).
  // https://www.barcodesinc.com/media/pdf/Bosch/ds1101i.pdf
  {
    id: 'bosch-ds1101i',
    manufacturer: 'Bosch',
    model: 'DS1101i',
    category: 'glass-break',
    tagline: 'Round acoustic glass-break — 7.6 m radius, plate/tempered/laminated/wired',
    verified: true,
    datasheetUrl: 'https://www.barcodesinc.com/media/pdf/Bosch/ds1101i.pdf',
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 7.6,
    },
  },

  // DS1102i — Low-temp acoustic glass-break (-25 °C to +49 °C).
  // Source: DS1100i Series datasheet (Bosch mirror).
  // https://www.barcodesinc.com/media/pdf/Bosch/ds1101i.pdf
  {
    id: 'bosch-ds1102i',
    manufacturer: 'Bosch',
    model: 'DS1102i',
    category: 'glass-break',
    tagline: 'Low-temp acoustic glass-break — 7.6 m radius, rated to -25 °C',
    verified: true,
    datasheetUrl: 'https://www.barcodesinc.com/media/pdf/Bosch/ds1101i.pdf',
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 7.6,
    },
  },

  // DS1108i — Flush-mount acoustic glass-break, 25 ft (7.6 m).
  // Source: DS1108i datasheet (Bosch via jmac mirror).
  // https://jmacfiles.s3.amazonaws.com/DS1108I-DataSheet.pdf
  {
    id: 'bosch-ds1108i',
    manufacturer: 'Bosch',
    model: 'DS1108i',
    category: 'glass-break',
    tagline: 'Flush-mount acoustic glass-break — 7.6 m radius, SAT sound analysis',
    verified: true,
    datasheetUrl: 'https://jmacfiles.s3.amazonaws.com/DS1108I-DataSheet.pdf',
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 7.6,
    },
  },

  // ISC-GBD2-PS — Conventional acoustic glass-break, current EU SKU.
  // Source: ISC-GBD2-PS datasheet (Bosch Azure CDN).
  // http://resource.boschsecurity.com/documents/ISC_GBD2_PS_Data_sheet_enUS_1419433739.pdf
  {
    id: 'bosch-isc-gbd2-ps',
    manufacturer: 'Bosch',
    model: 'ISC-GBD2-PS',
    category: 'glass-break',
    tagline: 'Conventional acoustic glass-break — 2 m radius, EN 50131-2-7-1 Grade 2',
    verified: true,
    datasheetUrl:
      'http://resource.boschsecurity.com/documents/ISC_GBD2_PS_Data_sheet_enUS_1419433739.pdf',
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 2,
    },
  },

  // ISP-GBD2-P110S — LSN passive (vibration) glass-break, VdS Class B.
  // Source: ISP-GBD2-P110S datasheet (Bosch CDN via resource.boschsecurity).
  // http://resource.boschsecurity.com/documents/Data_sheet_enUS_1414404875.pdf
  {
    id: 'bosch-isp-gbd2-p110s',
    manufacturer: 'Bosch',
    model: 'ISP-GBD2-P110S',
    category: 'glass-break',
    tagline: 'LSN passive piezo glass-break — 2 m radius, VdS-B, panel-side LSN bus',
    verified: true,
    datasheetUrl:
      'http://resource.boschsecurity.com/documents/Data_sheet_enUS_1414404875.pdf',
    specs: {
      method: 'piezo-shock',
      glassTypes: ['plain', 'tempered', 'laminated'],
      rangeM: 2,
    },
  },

  // RFGB-A — RADION wireless acoustic glass-break, 433.42 MHz.
  // Source: RFGB-A datasheet (Bosch mirror alldataresource).
  // https://www.alldataresource.com/assets/pdf_bosch/Bosch-RFGB-A-Wireless-Acoustic-Detector-Data-Sheet.pdf
  {
    id: 'bosch-rfgb-a',
    manufacturer: 'Bosch',
    model: 'RFGB-A',
    category: 'glass-break',
    tagline: 'RADION wireless acoustic glass-break — 433.42 MHz, 5 yr CR123A, tamper',
    verified: true,
    datasheetUrl:
      'https://www.alldataresource.com/assets/pdf_bosch/Bosch-RFGB-A-Wireless-Acoustic-Detector-Data-Sheet.pdf',
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // SMOKE / HEAT DETECTORS
  // ───────────────────────────────────────────────────────────────────────

  // D7050 — Addressable photoelectric smoke detector head (panel-connected).
  // Compatible with FPD-7024, D7024, DS7400Xi, G-Series fire/intrusion panels.
  // Source: D7050 datasheet (Bosch CDN).
  // https://resources-boschsecurity-cdn.azureedge.net/public/documents/Data_sheet_enUS_2700120843.pdf
  {
    id: 'bosch-d7050',
    manufacturer: 'Bosch',
    model: 'D7050',
    category: 'smoke-detector',
    tagline: 'Addressable photoelectric smoke head — UL Listed, FPD-7024 / D9412GV4 compatible',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/Data_sheet_enUS_2700120843.pdf`,
    specs: {
      detection: ['smoke-optical'],
      standalone: false,
    },
  },

  // D7050TH — Photoelectric smoke + thermal (fixed + ROR heat) detector head.
  // Source: same D7050 datasheet (D7050 and D7050TH share the bulletin).
  // https://www.lifesafetycom.com/wp-content/uploads/2019/02/Bosch-D7050TH-Photoelectric-Smoke-and-Heat-Detector.pdf
  {
    id: 'bosch-d7050th',
    manufacturer: 'Bosch',
    model: 'D7050TH',
    category: 'smoke-detector',
    tagline: 'Addressable photoelectric smoke + fixed/ROR heat head — UL Listed, multi-criteria',
    verified: true,
    datasheetUrl:
      'https://www.lifesafetycom.com/wp-content/uploads/2019/02/Bosch-D7050TH-Photoelectric-Smoke-and-Heat-Detector.pdf',
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise'],
      standalone: false,
    },
  },

  // RFSM-A — RADION wireless photoelectric smoke + heat (fixed + ROR).
  // Source: RFSM-A datasheet (Bosch mirror leewayautomation).
  // https://leewayautomation.com.au/assets/uploads/2023/01/RFSM.pdf
  {
    id: 'bosch-rfsm-a',
    manufacturer: 'Bosch',
    model: 'RFSM-A',
    category: 'smoke-detector',
    tagline: 'RADION wireless smoke + heat — photoelectric + ROR/fixed, 10 yr battery, 433.42 MHz',
    verified: true,
    datasheetUrl: 'https://leewayautomation.com.au/assets/uploads/2023/01/RFSM.pdf',
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise'],
      standalone: false,
      batteryYears: 10,
      wirelessSystem: 'RADION 433.42 MHz',
    },
  },

  // RFSM2-A — RADION wireless photoelectric smoke (current SKU, smoke-only).
  // Source: RFSM2-A product page (Bosch commerce).
  // https://commerce.boschsecurity.com/us/en/RADION-smoke/p/F.01U.323.775/
  {
    id: 'bosch-rfsm2-a',
    manufacturer: 'Bosch',
    model: 'RFSM2-A',
    category: 'smoke-detector',
    tagline: 'RADION wireless smoke — photoelectric, supervised, RADION 433.42 MHz',
    verified: true,
    datasheetUrl:
      'https://commerce.boschsecurity.com/us/en/RADION-smoke/p/F.01U.323.775/',
    specs: {
      detection: ['smoke-optical'],
      standalone: false,
      batteryYears: 10,
      wirelessSystem: 'RADION 433.42 MHz',
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // PANIC BUTTONS — RADION wireless transmitters
  // ───────────────────────────────────────────────────────────────────────

  // RFPB-SB-A — RADION wireless single-button panic transmitter.
  // Source: RFPB-SB-A product page (Bosch commerce US).
  // https://commerce.boschsecurity.com/us/en/RADION-panic/p/F.01U.323.792/
  {
    id: 'bosch-rfpb-sb-a',
    manufacturer: 'Bosch',
    model: 'RFPB-SB-A',
    category: 'panic-button',
    tagline: 'RADION wireless 1-button panic — 600 ms hold, supervised, 5 yr battery, IP54',
    verified: true,
    datasheetUrl:
      'https://commerce.boschsecurity.com/us/en/RADION-panic/p/F.01U.323.792/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      accidentalPressProtection: true,
      tamper: true,
    },
  },

  // RFPB-TB-A — RADION wireless two-button panic transmitter (both buttons
  // must be held for ≥600 ms to trigger, preventing single-finger accidents).
  // Source: RFPB-TB-A product page (Bosch commerce US).
  // https://commerce.boschsecurity.com/us/en/RADION-panic/p/F.01U.323.793/
  {
    id: 'bosch-rfpb-tb-a',
    manufacturer: 'Bosch',
    model: 'RFPB-TB-A',
    category: 'panic-button',
    tagline: 'RADION wireless 2-button panic — dual-press required, anti-accidental, supervised',
    verified: true,
    datasheetUrl:
      'https://commerce.boschsecurity.com/us/en/RADION-panic/p/F.01U.323.793/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      accidentalPressProtection: true,
      tamper: true,
    },
  },

  // RFPB-FP-A — RADION wireless fixed-position panic switch (silent hold-up
  // for high-security retail / banking applications, mechanically latching).
  // Source: RFPB-FP-A datasheet (Bosch via Nexlar mirror).
  // https://www.nexlar.com/shop/bosch-security-systems-rfpb-fp-a-wireless-panic-button-fixed-position/
  {
    id: 'bosch-rfpb-fp-a',
    manufacturer: 'Bosch',
    model: 'RFPB-FP-A',
    category: 'panic-button',
    tagline: 'RADION wireless fixed-position hold-up switch — mechanical latch, supervised, silent',
    verified: true,
    datasheetUrl:
      'https://www.nexlar.com/shop/bosch-security-systems-rfpb-fp-a-wireless-panic-button-fixed-position/',
    specs: {
      memory: 'mechanical-latch',
      connection: 'wireless',
      accidentalPressProtection: true,
      tamper: true,
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // ACCESS CONTROL — AMC2 family
  // ───────────────────────────────────────────────────────────────────────

  // AMC2 — Access Modular Controller, host bus to BIS/AMS via TCP/IP.
  // Standalone capacity: up to 200,000 cards. Controls 1-8 access points
  // depending on reader interface board variant.
  // Source: AMC2 datasheet (Bosch CDN).
  // https://cdn.commerce.boschsecurity.com/public/documents/AMC2_Data_sheet_enUS_1354080523.pdf
  {
    id: 'bosch-amc2',
    manufacturer: 'Bosch',
    model: 'AMC2 — Access Modular Controller',
    category: 'door-controller',
    tagline: 'IP-connected modular access controller — 200K cards, 1-8 access points, BIS/AMS',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/AMC2_Data_sheet_enUS_1354080523.pdf`,
    specs: {
      doors: 2, // pair-mode default; chassis scales 1-8 access points via boards
      hostBus: 'tcp-ip',
      fireAlarmRelease: true,
    },
  },

  // APC-AMC2-4WCF — AMC2 with 4× Wiegand reader interfaces + Compact Flash.
  // Source: AMC2 4WCF installation manual (Bosch CDN).
  // https://cdn.commerce.boschsecurity.com/public/documents/4WCF_Installation_Manual_enUS_85392978955.pdf
  {
    id: 'bosch-amc2-4wcf',
    manufacturer: 'Bosch',
    model: 'APC-AMC2-4WCF',
    category: 'door-controller',
    tagline: 'AMC2 Wiegand variant — 4 reader inputs, 2 GB CF, 200K cards, TCP/IP host',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/4WCF_Installation_Manual_enUS_85392978955.pdf`,
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      fireAlarmRelease: true,
    },
  },

  // APC-AMC2-4R4CF — AMC2 with 4× RS-485 (OSDPv2 secure) reader interfaces.
  // Source: AMC2 datasheet (4R4 variant uses OSDPv2 secure protocol).
  // https://cdn.commerce.boschsecurity.com/public/documents/AMC2_Data_sheet_enUS_1354080523.pdf
  {
    id: 'bosch-amc2-4r4cf',
    manufacturer: 'Bosch',
    model: 'APC-AMC2-4R4CF',
    category: 'door-controller',
    tagline: 'AMC2 OSDPv2 variant — 4× RS-485, up to 8 readers, secure bidirectional',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/AMC2_Data_sheet_enUS_1354080523.pdf`,
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
      fireAlarmRelease: true,
    },
  },

  // APC-AMC2-16ION — AMC2 expansion I/O board variant (16 inputs + outputs)
  // — used as an I/O extension for AMC2 4WCF/4R4CF controllers, not a
  // reader interface. Useful when designing for systems with many
  // intrusion / lock / aux contacts on a single access controller.
  // Source: AMC2 16ION installation manual (Bosch CDN).
  // https://cdn.commerce.boschsecurity.com/public/documents/AMC2_16ION_Installation_Manual_enUS_17921453323.pdf
  {
    id: 'bosch-amc2-16ion',
    manufacturer: 'Bosch',
    model: 'API-AMC2-16ION',
    category: 'door-controller',
    tagline: 'AMC2 I/O extension — 16 inputs + 16 outputs, RS-485 to AMC2 host',
    verified: true,
    datasheetUrl: `${BOSCH_ASSETS}/AMC2_16ION_Installation_Manual_enUS_17921453323.pdf`,
    specs: {
      doors: 2,
      hostBus: 'rs-485-satel', // RS-485 to parent AMC2 (not SATEL — naming reused)
      fireAlarmRelease: true,
    },
  },
];
